import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import '@/styles/createPasswordStyles.scss';
import * as Sentry from '@sentry/react';

const CreatePassword = () => {
  const { apiFetch } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [valid, setValid] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ password: "", confirmPassword: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      const timer = setTimeout(() => {
        setValid(false);
        setMessage("No token provided.");
        setLoading(false);
      }, 0);
      return () => clearTimeout(timer);
    }

    const validateToken = async () => {
      try {
        const response = await apiFetch(`/api/User/validate-create-password-token?token=${encodeURIComponent(token)}`);
        const data = await response.json();
        // API returns a structure like { valid: boolean, message?: string }
        setValid(Boolean(data.valid));
        if (!data.valid) setMessage(data.message || "Invalid token.");
      } catch (catchError) {
          Sentry.captureException(catchError);
        setValid(false);
        setMessage("Server error.");
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, apiFetch]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    if (!form.password || !form.confirmPassword) {
      setError("Both fields are required.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiFetch("/api/User/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          newPassword: form.password,
          confirmPassword: form.confirmPassword
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setTimeout(() => navigate("/"), 2500);
      } else {
        setError(data.message || "Failed to set password.");
      }
    } catch (catchError) {
        Sentry.captureException(catchError);
      setError("Server error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="create-password-container"><div>Loading...</div></div>;
  if (!valid) return <div className="create-password-container"><div className="error-message">{message || "Email link expired."}</div></div>;
  if (success) return <div className="create-password-container"><div className="success-message">Password set successfully! Redirecting to login...</div></div>;

  return (
    <div className="create-password-container">
      <div className="create-password-card">
        <h2>Create Your Password</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              minLength={8}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              minLength={8}
              required
              autoComplete="new-password"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Setting Password..." : "Set Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreatePassword;
